import { useMemo, useState } from 'react';
import type { HistoryData, Team } from '~/vendor/cxy/types';

import { filterHistoryData } from './filterHistoryData';

interface UseBarChartProps {
  historyData: HistoryData[];
  limit?: number;
  oppositionTeam?: Team;
  trackEvent?: (eventName: string, eventData: Record<string, unknown>) => void;
}

export function useBarChart({ historyData, limit, oppositionTeam, trackEvent }: UseBarChartProps) {
  const [selectedFilterType, setSelectedFilterType] = useState('All');

  const filteredData = useMemo(
    () => filterHistoryData(historyData, selectedFilterType, oppositionTeam, limit),
    [historyData, selectedFilterType, oppositionTeam, limit],
  );

  // `Math.max()` of nothing is -Infinity, which reached the y-axis domain and the width below as
  // NaN. Zero is the honest answer for an empty chart.
  //
  // NOTE this is still the SIGNED maximum. For the bidirectional chart, which uses `[-max, max]`, an
  // all-negative or lopsided dataset therefore gets a domain that clips or inverts — that needs the
  // maximum MAGNITUDE, which changes the rendered axis for real data and so wants visual sign-off
  // rather than being folded into a correctness pass.
  const maxBetStat = useMemo(
    () => (historyData.length === 0 ? 0 : Math.max(...historyData.map(item => item.participant.betStat))),
    [historyData],
  );

  const yAxisWidth = useMemo(() => {
    return (maxBetStat.toString().length + 1) * 8;
  }, [maxBetStat]);

  const shouldShowVersusTab = useMemo(() => {
    return filteredData.some(
      item =>
        item.homeTeam.participantId === oppositionTeam?.participantId ||
        item.awayTeam.participantId === oppositionTeam?.participantId,
    );
  }, [filteredData, oppositionTeam]);

  const changeFilterType = (value: string) => {
    trackEvent?.('match_venue_change', {
      eventLabel: 'Match Venue Change',
      selection: value,
    });
    setSelectedFilterType(value);
  };

  return {
    selectedFilterType,
    filteredData,
    maxBetStat,
    yAxisWidth,
    shouldShowVersusTab,
    changeFilterType,
  };
}
